import type {ReactElement, RefObject} from 'react';
import {useRef, useEffect, useState} from 'react';
import {Text, TextSize} from '@Components/text';
import type {SocialAccount} from '@Components/social-media/account.vo';
import {isThreadsAccount, isGoogleBusinessProfileAccount, isPinterestAccount} from '@Components/social-media/account.vo';
import type {GraphicMeta, GraphicsMetaMapType, PosterHashedId, PageHashedId} from '@Components/social-media/publish-params.types';
import {noop} from '@Utils/general.util';
import {getCacheBustedURL} from '@Utils/url.util';
import {isVideoUrl} from '@Utils/media.util';
import {doesAccountHaveCarouselPosts, seekAndPauseAutoplayVideo} from '@Libraries/social-media';
import {SimpleMediaCarousel} from '@Components/simple-media-carousel/simple-media-carousel';
import type {MediaItemData} from '@Components/simple-media-carousel/simple-media-carousel.types';
import {isIOS} from 'react-device-detect';
import {pollForUpdatesInURL} from '@Libraries/poster-library';
import styles from './social-post-content-media-wrapper.module.scss';
import {PreviewVideoPlayer} from '../../preview-video-player/preview-video-player';
import {ClickableDiv} from '@Components/clickable-div';
import type {DesignSelection} from '@/components/social-media/social-media.types';
import useDidUpdateEffect from '@Hooks/useDidUpdateEffect';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';

export interface SocialPostContentMediaWrapperProps {
  onAssetLoad?: (design: DesignSelection) => void;
  onAssetErrored?: (design: DesignSelection) => void;
  doRetryOnError?: boolean;
  account?: SocialAccount;
  designs: DesignSelection[];
  graphicsLoadingMap: Record<PosterHashedId, Record<PageHashedId, boolean>>;
  onOrphanedPreviewContainerClick?: VoidFunction;
  designsGraphicsMetaMap: GraphicsMetaMapType;
  enableVideoAutoplay?: boolean;
}

export function SocialPostContentMediaWrapper({
  onAssetLoad = noop,
  onAssetErrored = noop,
  doRetryOnError = false,
  account,
  designs,
  graphicsLoadingMap,
  onOrphanedPreviewContainerClick = noop,
  designsGraphicsMetaMap,
  enableVideoAutoplay = false,
}: SocialPostContentMediaWrapperProps): ReactElement<any> {
  const [designsGraphicsMetaMapState, setDesignsGraphicsMetaMapState] = useState<GraphicsMetaMapType>({});
  const [retryThroughRefreshNumber, setRetryThroughRefreshNumber] = useState(0);
  const [isCacheBustingNeeded, setIsCacheBustingNeeded] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const maxNumberDisplayablePosters = 3;
  const maxNumberRows = 2;
  const minNumberRows = 1;
  const minNumberDisplayablePosters = 1;
  const maxRetries = 40;
  const retryTimeoutInMS = 5000;
  const isOrphanedPreview = !account;

  const haveAllPostersLoaded = (): boolean => {
    return Object.values(graphicsLoadingMap).every((pageLoadingMap) => {
      return Object.values(pageLoadingMap).every((isPageLoading) => isPageLoading);
    });
  };

  const isAnyPosterLoaded = (): boolean => {
    return Object.values(graphicsLoadingMap).some((pageLoadingMap) => {
      return Object.values(pageLoadingMap).some((isPageLoading) => isPageLoading);
    });
  };

  useDidUpdateEffect(() => {
    setDesignsGraphicsMetaMapState({});
  }, [account]);

  const shouldResetGraphicsMeta = (): boolean => {
    return Object.values(designsGraphicsMetaMap).some((pageGraphicsMetaMap) => {
      return Object.values(pageGraphicsMetaMap).some((graphicsMeta) => graphicsMeta.graphicURL === '');
    });
  };

  useEffect((): void => {
    if (haveAllPostersLoaded() || isAnyPosterLoaded()) {
      return;
    }

    if (shouldResetGraphicsMeta()) {
      setDesignsGraphicsMetaMapState({});
    } else {
      setDesignsGraphicsMetaMapState(designsGraphicsMetaMap);
    }
  }, [graphicsLoadingMap]);

  const getNumberOfImages = (): number => {
    return designs.length;
  };

  const isMultiPosting = (): boolean => {
    return designs.length > 1;
  };

  const getClassesForHolder = (): string => {
    if (isPinterestAccount(account)) {
      return styles.pinterest;
    }

    if (isGoogleBusinessProfileAccount(account)) {
      return styles.googleBusinessProfile;
    }

    if (getNumberOfImages() > minNumberDisplayablePosters) {
      return styles.multipleImages;
    }

    return '';
  };

  const getClassesForAsset = (imageNumber: number): string => {
    const numberImages = getNumberOfImages();
    if (numberImages === minNumberDisplayablePosters) {
      return '';
    }

    if (imageNumber === 1) {
      return styles.multipleImages;
    }

    if (numberImages === 2) {
      return styles.twinImages;
    }

    return styles.lastRow;
  };

  const isLastRow = (rowIndex: number): boolean => {
    return rowIndex === 1;
  };

  const isOverlayNeeded = (rowIndex: number): boolean => {
    return isLastRow(rowIndex) && getNumberOfImages() > 3;
  };

  const getClassesForAssetHolder = (rowIndex: number): string => {
    const numberImages = getNumberOfImages();
    if (numberImages === 1) {
      return '';
    }

    let assetHolderStyles = styles.multipleImages;

    if (isOverlayNeeded(rowIndex)) {
      assetHolderStyles = `${assetHolderStyles} ${styles.overlayHolder}`;
    }

    return assetHolderStyles;
  };

  const getNumberOfImagesPerRow = (numberRows: number): number[] => {
    if (numberRows === 1) {
      return [1];
    }

    const numberImages = getNumberOfImages();
    if (numberImages === 2) {
      return [1, 1];
    }

    return [1, 2];
  };

  const onLoad = ({posterHashedId, pageHashedId}: DesignSelection): void => {
    const urlToPoll = designsGraphicsMetaMapState[posterHashedId][pageHashedId].graphicURL;
    pollForUpdatesInURL(urlToPoll, true)
      .promise.then((): void => {
        onAssetLoad({posterHashedId: posterHashedId, pageHashedId: pageHashedId});
        setIsCacheBustingNeeded(false);
      })
      .catch((e: unknown) => {
        console.error(e);
      });
  };

  const getClassesForAssetWrapper = (imageNumber: number): string => {
    const isFirstImage = imageNumber === 1;

    if (!isFirstImage && getNumberOfImages() > 2) {
      return styles.assetWrapperTwin;
    }

    let styleClasses = styles.assetWrapper;

    if (isGoogleBusinessProfileAccount(account)) {
      styleClasses += ` ${styles.googleBusinessProfile}`;
    }

    if (isThreadsAccount(account) && !isMultiPosting()) {
      styleClasses += ` spacing-m-l-12`;
    }

    return styleClasses;
  };

  const getAssetElement = (imageNumber: number): ReactElement<any> => {
    return (
      <div key={`asset-${imageNumber}`} className={getClassesForAssetWrapper(imageNumber)}>
        {getMedia(imageNumber)}
      </div>
    );
  };

  const getCarouselCounterClasses = (): string => {
    if (isPinterestAccount(account)) {
      return styles.pinterestCarouselCounter;
    }

    return '';
  };

  const getCarouselIndicatorClasses = (): string => {
    if (isPinterestAccount(account)) {
      return styles.pinterestCarouselIndicator;
    }

    return '';
  };

  const getCarouselContainerClasses = (): string => {
    if (isPinterestAccount(account)) {
      return '_borderless';
    }

    return styles.threadsAndInstagramCarouselContainer;
  };

  const getAssetIndex = (assetNumber: number): number => {
    return assetNumber - 1;
  };


  const getGraphicURL = (url: string): string => {
    if (isCacheBustingNeeded) {
      return getCacheBustedURL(url);
    }

    return url;
  };

  const getCoverURL = (posterGraphicMeta: GraphicMeta): string | undefined => {
    const coverObject = posterGraphicMeta.cover;

    if (!coverObject) {
      return undefined;
    }

    return coverObject.coverUrl;
  };

  const getVideoPreviewWithoutCoverForIOS = (assetNumber: number, design: DesignSelection, graphicURL: string): ReactElement<any> => {
    const FIRST_FRAME_SEC = 0.1;

    return (
      <>
        <video
          id={`preview-grid-image-${assetNumber}`}
          className={`${styles.assetStyles} ${getClassesForAsset(assetNumber)}`}
          ref={videoRef}
          onLoadedData={(e) => {
            seekAndPauseAutoplayVideo(e, FIRST_FRAME_SEC);
            onLoad(design);
          }}
          onError={() => {
            onAssetErrored(design);
            doRetryIfNeeded();
          }}
          muted
          loop
          src={graphicURL}
          autoPlay
          playsInline
        />
        <PreviewVideoPlayer videoRef={videoRef} showSoundControls={false} />
      </>
    );
  };

  const getPreviewVideoPlayerIfNeeded = (videoRef: RefObject<HTMLVideoElement | null>): ReactElement<any> => {
    if (enableVideoAutoplay) {
      return <></>;
    }

    return <PreviewVideoPlayer videoRef={videoRef} showSoundControls={false} />;
  };

  const getVideoMedia = (assetNumber: number, design: DesignSelection, graphicURL: string, coverUrl: string | undefined): ReactElement<any> => {
    if (isOrphanedPreview && !enableVideoAutoplay && !isVideoUrl(graphicURL)) {
      return (
        <>
          <div className={styles.videoIconContainer} aria-hidden="true">
            <Icon className={'content-body-white'} icon={'icon-play-video'} size={IconSize.SIZE_ICON_24} />
          </div>
          <img
            id={`preview-grid-image-${assetNumber}`}
            className={`${styles.assetStyles} ${getClassesForAsset(assetNumber)}`}
            onLoad={() => {
              onLoad(design);
            }}
            onError={() => {
              onAssetErrored(design);
              doRetryIfNeeded();
            }}
            src={coverUrl ?? graphicURL}
            alt={window.i18next.t('pmwjs_social_media_graphic')}
          />
        </>
      );
    }

    if (isIOS && !coverUrl) {
      return getVideoPreviewWithoutCoverForIOS(assetNumber, design, graphicURL);
    }

    return (
      <>
        <video
          id={`preview-grid-image-${assetNumber}`}
          className={`${styles.assetStyles} ${getClassesForAsset(assetNumber)}`}
          ref={videoRef}
          onLoadedMetadata={() => {
            onLoad(design);
          }}
          onError={() => {
            onAssetErrored(design);
            doRetryIfNeeded();
          }}
          poster={coverUrl}
          muted
          loop
          src={graphicURL}
          autoPlay={enableVideoAutoplay}
        />
        {getPreviewVideoPlayerIfNeeded(videoRef)}
      </>
    );
  };

  const getMedia = (assetNumber: number): ReactElement<any> => {
    const assetIndex = getAssetIndex(assetNumber);

    if (assetIndex >= designs.length) {
      return <></>;
    }

    const design = designs[assetIndex];
    const designGraphicMeta = designsGraphicsMetaMapState?.[design.posterHashedId]?.[design.pageHashedId];

    let isVideo = false;
    let graphicURL = '';
    let coverUrl;

    if (designGraphicMeta?.graphicURL) {
      isVideo = designGraphicMeta.isVideo;
      graphicURL = getGraphicURL(designGraphicMeta.graphicURL);
      coverUrl = getCoverURL(designGraphicMeta);
    }

    if (isVideo) {
      return getVideoMedia(assetNumber, design, graphicURL, coverUrl);
    }

    return (
      <img
        id={`preview-grid-image-${assetNumber}`}
        className={`${styles.assetStyles} ${getClassesForAsset(assetNumber)}`}
        onLoad={() => {
          onLoad(design);
        }}
        onError={() => {
          onAssetErrored(design);
          doRetryIfNeeded();
        }}
        src={graphicURL}
        alt={window.i18next.t('pmwjs_social_media_graphic')}
      />
    );
  };

  const doRetryIfNeeded = (): void => {
    if (doRetryOnError) {
      retrySetGraphicURL();
    }
  };

  const retrySetGraphicURL = (): void => {
    if (areMaxRetriesReached()) {
      return;
    }

    if (!isCacheBustingNeeded) {
      setIsCacheBustingNeeded(true);
    }

    setTimeout((): void => {
      setRetryThroughRefreshNumber(retryThroughRefreshNumber + 1);

      if (areMaxRetriesReached()) {
        setIsCacheBustingNeeded(false);
      }
    }, retryTimeoutInMS);
  };

  const areMaxRetriesReached = (): boolean => {
    return retryThroughRefreshNumber >= maxRetries;
  };

  const getImageNumberInRow = (nRow: number, rowImageIndex: number): number => {
    if (nRow === 0) {
      return 1;
    }

    return 2 + rowImageIndex;
  };

  const getImageGridRow = (nRowImages: number, nRow: number): ReactElement<any>[] => {
    const rowElements = [];

    for (let i = 0; i < nRowImages; i++) {
      const imageNumber = getImageNumberInRow(nRow, i);
      rowElements.push(getAssetElement(imageNumber));
    }

    return rowElements;
  };

  const getNumberHiddenImages = (): number => {
    return getNumberOfImages() - maxNumberDisplayablePosters;
  };

  const getOverlay = (rowIndex: number): ReactElement<any> | null => {
    if (!isOverlayNeeded(rowIndex)) {
      return null;
    }

    return (
      <div className={styles.overlay}>
        <Text size={TextSize.XLARGE} className="content-body-white" bold val={`+${getNumberHiddenImages()}`} />
      </div>
    );
  };

  const getImageGrid = (): ReactElement<any>[] => {
    const numberRows = getNumberOfImages() === minNumberDisplayablePosters ? minNumberRows : maxNumberRows;
    const numberImagesPerRow = getNumberOfImagesPerRow(numberRows);
    const gridElements = [];

    for (let i = 0; i < numberRows; i++) {
      const nImages = numberImagesPerRow[i];
      gridElements.push(
        <div key={`asset-media-grid-${i}`} className={`${styles.assetHolder} ${getClassesForAssetHolder(i)}`}>
          {getImageGridRow(nImages, i)}
          {getOverlay(i)}
        </div>
      );
    }

    return gridElements;
  };

  const getMediaItemsForCarousel = (): MediaItemData[] => {
    const unorderedMediaItems: (MediaItemData & {order?: number})[] = [];

    Object.keys(designsGraphicsMetaMapState).forEach((posterHashId) => {
      Object.keys(designsGraphicsMetaMapState[posterHashId]).forEach((pageHashId) => {
        unorderedMediaItems.push({
          mediaUrl: getGraphicURL(designsGraphicsMetaMapState[posterHashId][pageHashId].graphicURL),
          isVideo: designsGraphicsMetaMapState[posterHashId][pageHashId].isVideo,
          externalMediaDesign: {posterHashedId: posterHashId, pageHashedId: pageHashId},
          order: designsGraphicsMetaMapState[posterHashId][pageHashId].order,
        });
      });
    });

    unorderedMediaItems.sort((a, b) => {
      const orderA = a.order ?? 0;
      const orderB = b.order ?? 0;
      return orderA - orderB;
    });

    return unorderedMediaItems.map((item) => {
      return {
        mediaUrl: item.mediaUrl,
        isVideo: item.isVideo,
        externalMediaDesign: item.externalMediaDesign,
      } as MediaItemData;
    });
  };

  const getContainerForCarouselPost = (): ReactElement<any> => {
    return (
      <SimpleMediaCarousel
        mediaItems={getMediaItemsForCarousel()}
        onIndividualImageLoad={onLoad}
        onIndividualVideoLoad={onLoad}
        onIndividualAssetErrored={onAssetErrored}
        doRetryOnIndividualAssetError={doRetryIfNeeded}
        counterClasses={getCarouselCounterClasses()}
        carouselIndicatorClasses={getCarouselIndicatorClasses()}
        containerClasses={getCarouselContainerClasses()}
      />
    );
  };

  if (isOrphanedPreview) {
    return (
      <ClickableDiv className={`${styles.loadingPostHolder} ${getClassesForHolder()} _full-width _cursor-pointer`} onClick={onOrphanedPreviewContainerClick}>
        {getImageGrid()}
      </ClickableDiv>
    );
  }

  if (doesAccountHaveCarouselPosts(account?.type) && isMultiPosting()) {
    return <div className={`${styles.loadingPostHolder} ${getClassesForHolder()} _full-width`}>{getContainerForCarouselPost()}</div>;
  }

  return <div className={`${styles.loadingPostHolder} ${getClassesForHolder()} _full-width`}>{getImageGrid()}</div>;
}
